import { useMutation } from '@apollo/client';
import type { UseSegmentsOperation } from '@openx/components-segment/lib/utils/context/SegmentHookTypes';
import type { RenameSegmentRowActionPayload, SegmentOperationResult } from '@openx/components-segment/types';
import { type ListSegment, type SegmentRowActionType, segmentRowActionType } from '@openx/types/segments';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context';
import { CreateLogDocument } from '../../../graphql/mutation/error/createLog.generated';
import { UpdateSegmentDocument } from '../../../graphql/mutation/segments/UpdateSegment.generated';
import { createFullName } from '../../../modules/segments/utils/createFullName';

const finishedActionLabel = {
  [segmentRowActionType.DELETE]: 'archived',
  [segmentRowActionType.RENAME]: 'renamed',
};

export const useSegmentsOperation: UseSegmentsOperation = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [updateSegment] = useMutation(UpdateSegmentDocument);
  const [pushLog] = useMutation(CreateLogDocument);

  const actions = useMemo(() => {
    const refetchQueries = ['GetSegmentsList', 'GetSegmentFeed'];

    return {
      [segmentRowActionType.DELETE]: ({ id }: ListSegment) =>
        updateSegment({
          refetchQueries,
          variables: {
            set: { is_archived: true },
            where: { id: { _eq: id } },
          },
        }),

      [segmentRowActionType.RENAME]: ({ id, name, newName }: RenameSegmentRowActionPayload) => {
        const segmentName = newName ?? name;
        return updateSegment({
          refetchQueries,
          variables: {
            set: { full_name: createFullName(segmentName), name: segmentName },
            where: { id: { _eq: id } },
          },
        });
      },
    };
  }, [updateSegment]);

  const performSegmentAction = useCallback(
    async <T extends ListSegment>(segments: T[], action: SegmentRowActionType): Promise<SegmentOperationResult> => {
      const fulfilled: string[] = [];
      const rejected: string[] = [];

      const promises = segments.map(async segment => {
        try {
          await actions[action](segment);

          fulfilled.push(segment.id);
        } catch (_error) {
          rejected.push(segment.id);

          const location = window.location.href;
          await pushLog({
            variables: {
              email: currentUser?.email,
              location: location,
              msg: `Failed to archive segment${segments.length > 1 ? 's' : ''}`,
              name: currentUser?.displayName,
            },
          });
        }
      });

      await Promise.all(promises);

      if (segments.length === 1 && fulfilled.length) {
        const message = t('Segment {name} successfully {finishedAction}.', {
          finishedAction: finishedActionLabel[action],
          name: segments[0].name,
        });
        enqueueSnackbar(message, { variant: 'success' });
      } else if (fulfilled.length === segments.length) {
        const message = t('Segments successfully {finishedAction}.', { finishedAction: finishedActionLabel[action] });
        enqueueSnackbar(message, { variant: 'success' });
      } else if (!fulfilled.length) {
        const message = t('Failed to {action} {segment}.', {
          action: action.toLowerCase(),
          segment: rejected.length === 1 ? 'segment' : 'segments',
        });

        enqueueSnackbar(message, {
          variant: 'error',
        });
      } else {
        const message = t('{archived} of {all} was successfully {finishedAction}.', {
          all: segments.length,
          archived: fulfilled.length,
          finishedAction: finishedActionLabel[action],
        });
        enqueueSnackbar(message, { variant: 'success' });
      }

      return { fulfilled, rejected };
    },
    [t, pushLog, actions, currentUser?.displayName, currentUser?.email]
  );

  const archive = useCallback(
    (segments: ListSegment[]) => performSegmentAction(segments, segmentRowActionType.DELETE),
    [performSegmentAction]
  );
  const rename = useCallback(
    (segments: RenameSegmentRowActionPayload[]) => performSegmentAction(segments, segmentRowActionType.RENAME),
    [performSegmentAction]
  );

  return { archive, rename };
};
